import React from "react";
import { cn } from "@/lib/utils";
import * as LucideIcons from "lucide-react";

interface LifecyclePhase {
  ageRange: string;
  label: string;
  products: string[];
  icon?: string;
}

interface LifecycleTimelineProps {
  title?: string;
  phases?: LifecyclePhase[];
  currentAge?: number;
  animated?: boolean;
  className?: string;
}

const DEFAULT_PHASES: LifecyclePhase[] = [
  { ageRange: "0-18", label: "Kindheit", products: ["Sparbuch", "Kindervorsorge"], icon: "Baby" },
  { ageRange: "18-28", label: "Ausbildung", products: ["Taggeld", "Bausparen"], icon: "GraduationCap" },
  { ageRange: "28-45", label: "Aufbau", products: ["Wertpapiere", "Immobilien"], icon: "TrendingUp" },
  { ageRange: "45-60", label: "Konsolidierung", products: ["Lebensversicherung", "Vorsorge"], icon: "Shield" },
  { ageRange: "60+", label: "Ruhestand", products: ["Pension", "Vermögenserhalt"], icon: "Palmtree" },
];

export function LifecycleTimeline({
  title = "Smarte Lebenszyklusplanung",
  phases = DEFAULT_PHASES,
  currentAge,
  animated = true,
  className,
}: LifecycleTimelineProps) {
  const getIcon = (iconName?: string) => {
    if (!iconName) return null;
    const Icon = (LucideIcons as any)[iconName];
    return Icon ? <Icon className="h-6 w-6" /> : null;
  };

  const getCurrentPhaseIndex = () => {
    if (!currentAge) return -1;
    return phases.findIndex((phase) => {
      const [min, max] = phase.ageRange.split("-").map(s => parseInt(s.replace("+", "")));
      if (phase.ageRange.includes("+")) {
        return currentAge >= min;
      }
      return currentAge >= min && currentAge <= (max || 100);
    });
  };

  const currentPhaseIndex = getCurrentPhaseIndex();

  return (
    <div className={cn("space-y-6", className)}>
      {title && (
        <h3 className="font-semibold text-lg text-center">{title}</h3>
      )}

      {/* Timeline */}
      <div className="relative">
        {/* Connection Line */}
        <div className="absolute top-10 left-8 right-8 h-1 bg-muted rounded-full" />
        <div 
          className="absolute top-10 left-8 h-1 bg-primary rounded-full transition-all duration-700"
          style={{ 
            width: currentPhaseIndex >= 0 
              ? `${((currentPhaseIndex + 0.5) / phases.length) * 100}%` 
              : '0%' 
          }}
        />

        {/* Phases */}
        <div className="relative flex justify-between">
          {phases.map((phase, index) => {
            const isActive = index === currentPhaseIndex;
            const isPast = index < currentPhaseIndex;
            
            return (
              <div
                key={phase.ageRange}
                className={cn(
                  "flex flex-col items-center text-center",
                  "w-1/5 min-w-0",
                  animated && "animate-in fade-in slide-in-from-bottom-2"
                )}
                style={animated ? { animationDelay: `${index * 100}ms` } : undefined}
              >
                {/* Icon Circle */}
                <div className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-all",
                  "border-4 bg-background",
                  isActive
                    ? "border-primary text-primary scale-110 shadow-lg"
                    : isPast
                    ? "border-primary/50 text-primary/70"
                    : "border-muted text-muted-foreground"
                )}>
                  {getIcon(phase.icon)}
                </div>

                {/* Age Range */}
                <span className={cn(
                  "text-xs font-semibold",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}>
                  {phase.ageRange}
                </span>

                {/* Label */}
                <span className={cn(
                  "text-sm font-medium mt-1",
                  isActive && "text-primary"
                )}>
                  {phase.label}
                </span>

                {/* Products */}
                <div className="mt-2 space-y-1">
                  {phase.products.map((product) => (
                    <span
                      key={product}
                      className={cn(
                        "block text-xs px-2 py-0.5 rounded-full",
                        isActive
                          ? "bg-primary/10 text-primary"
                          : isPast
                          ? "bg-muted text-muted-foreground"
                          : "bg-muted/50 text-muted-foreground/70"
                      )}
                    >
                      {product}
                    </span>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {currentAge && (
        <p className="text-center text-sm text-muted-foreground">
          Mit <span className="font-semibold text-primary">{currentAge} Jahren</span> befinden Sie sich in der Phase "{phases[currentPhaseIndex]?.label}"
        </p>
      )}
    </div>
  );
}
